import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';
import { EmojiModule, ExpansionPanelModule, ImageModel, Nullable, UploaderModule } from '@pin-up-global/common';
import { ProfileComponent } from '@shared/bot-user';
import { ChatComponent } from '@shared/chat';
import { tap } from 'rxjs';
import { FeedbackService } from './feedback.service';
import { EditorOptions, RichTextEditorComponent, ToolbarItemType } from '@shared/rich-text-editor';
import { fromImageModel } from '@shared/utils';
import { Feedback, FeedbackDialogData, FeedbackForm, FeedbackStatus } from './feedback.abstraction';
import { MatError } from '@angular/material/form-field';

@Component({
  selector: 'ca-feedback',
  standalone: true,
  imports: [
    MatIcon,
    MatIconButton,
    MatTabsModule,
    TranslateModule,
    AsyncPipe,
    MatProgressBar,
    ExpansionPanelModule,
    ProfileComponent,
    ChatComponent,
    MatButtonModule,
    EmojiModule,
    ReactiveFormsModule,
    MatDividerModule,
    RichTextEditorComponent,
    UploaderModule,
    MatError,
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FeedbackService],
})
export class FeedbackComponent implements OnInit {
  #data = inject<FeedbackDialogData>(MAT_DIALOG_DATA);
  #destroyRef = inject(DestroyRef);
  #matDialogRef = inject(MatDialogRef);
  #feedbackService = inject(FeedbackService);

  editorOptions: EditorOptions = { height: '130px' };
  editorButtons: ToolbarItemType[] = ['bold', 'italic', 'strike'];
  feedback: Nullable<Feedback> = null;

  form = new FormGroup<FeedbackForm>({
    text: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    images: new FormControl<Nullable<ImageModel[]>>([]),
  });

  isLoading$ = this.#feedbackService.isLoading$;
  profile$ = this.#feedbackService.user$;
  feedback$ = this.#feedbackService.feedback$.pipe(tap((res) => this.feedback = res));

  readonly FeedbackStatus = FeedbackStatus;

  ngOnInit(): void {
    this.#feedbackService.setFeedbackId(this.#data.feedbackId);
    this.#feedbackService.setUserId(this.#data.userId);
  }

  close(): void {
    if (this.feedback?.status === FeedbackStatus.New) {
      this.#feedbackService.changeFeedbackStatus(this.#data.feedbackId, FeedbackStatus.InProgress)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => this.#matDialogRef.close());

      return;
    }

    this.#matDialogRef.close();
  }

  onSendFeedback(): void {
    const formValue = { ...this.form.getRawValue() };
    const images = fromImageModel(formValue.images || []);

    this.#feedbackService
      .sendMessage(this.#data.feedbackId, { ...formValue, images })
      .pipe(
        tap(() => this.form.reset()),
        takeUntilDestroyed(this.#destroyRef)
      ).subscribe();
  }
}
